import { create } from "zustand";

// Interface for a Loadcase Group
export interface LoadcaseGroup {
  id: string;
  name: string;
  createdAt: number;
  // Each loadcase group has its own independent data
  beamLoadIds: string[];  // IDs of beam loads belonging to this group
  nodeLoadIds: string[];  // IDs of node loads belonging to this group
}

interface LoadcaseGroupState {
  // All loadcase groups
  loadcaseGroups: LoadcaseGroup[];
  // Currently active/selected loadcase group name
  activeLoadcaseName: string | null;
  
  // CRUD Operations
  createLoadcaseGroup: (name: string) => LoadcaseGroup;
  deleteLoadcaseGroup: (name: string) => void;
  renameLoadcaseGroup: (oldName: string, newName: string) => void;
  
  // Selection
  setActiveLoadcase: (name: string | null) => void;
  getActiveLoadcase: () => LoadcaseGroup | null;
  
  // Data association
  addBeamLoadToGroup: (loadcaseName: string, beamLoadId: string) => void;
  removeBeamLoadFromGroup: (loadcaseName: string, beamLoadId: string) => void;
  addNodeLoadToGroup: (loadcaseName: string, nodeLoadId: string) => void;
  removeNodeLoadFromGroup: (loadcaseName: string, nodeLoadId: string) => void;
  
  // Query
  getLoadcaseByName: (name: string) => LoadcaseGroup | undefined;
  getAllLoadcaseNames: () => string[];
  getBeamLoadIdsForLoadcase: (name: string) => string[];
  getNodeLoadIdsForLoadcase: (name: string) => string[];
}

export const useLoadcaseGroupStore = create<LoadcaseGroupState>()(
  (set, get) => ({
    loadcaseGroups: [],
    activeLoadcaseName: null,

      createLoadcaseGroup: (name: string) => {
        const trimmedName = name.trim();
        
        // Check if name already exists
        const existing = get().loadcaseGroups.find(
          (g) => g.name.toLowerCase() === trimmedName.toLowerCase()
        );
        
        if (existing) {
          throw new Error(`Loadcase "${trimmedName}" already exists`);
        }

        const newGroup: LoadcaseGroup = {
          id: `loadcase_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          name: trimmedName,
          createdAt: Date.now(),
          beamLoadIds: [],
          nodeLoadIds: [],
        };

        set((state) => ({
          loadcaseGroups: [...state.loadcaseGroups, newGroup],
          // Don't auto-activate - let the component decide
        }));

        return newGroup;
      },

      deleteLoadcaseGroup: (name: string) => {
        const state = get();
        const group = state.loadcaseGroups.find(
          (g) => g.name.toLowerCase() === name.toLowerCase()
        );

        if (!group) {
          console.warn(`⚠️ Loadcase "${name}" not found`);
          return;
        }

        set((state) => ({
          loadcaseGroups: state.loadcaseGroups.filter(
            (g) => g.name.toLowerCase() !== name.toLowerCase()
          ),
          // If we deleted the active loadcase, clear selection or select first available
          activeLoadcaseName:
            state.activeLoadcaseName?.toLowerCase() === name.toLowerCase()
              ? state.loadcaseGroups.length > 1
                ? state.loadcaseGroups.find(
                    (g) => g.name.toLowerCase() !== name.toLowerCase()
                  )?.name || null
                : null
              : state.activeLoadcaseName,
        }));
      },

      renameLoadcaseGroup: (oldName: string, newName: string) => {
        const trimmedNewName = newName.trim();
        
        // Check if new name already exists
        const existing = get().loadcaseGroups.find(
          (g) => g.name.toLowerCase() === trimmedNewName.toLowerCase() &&
                 g.name.toLowerCase() !== oldName.toLowerCase()
        );
        
        if (existing) {
          throw new Error(`Loadcase "${trimmedNewName}" already exists`);
        }

        set((state) => ({
          loadcaseGroups: state.loadcaseGroups.map((g) =>
            g.name.toLowerCase() === oldName.toLowerCase()
              ? { ...g, name: trimmedNewName }
              : g
          ),
          activeLoadcaseName:
            state.activeLoadcaseName?.toLowerCase() === oldName.toLowerCase()
              ? trimmedNewName
              : state.activeLoadcaseName,
        }));
      },

      setActiveLoadcase: (name: string | null) => {
        if (name === null) {
          set({ activeLoadcaseName: null });
          return;
        }

        const group = get().loadcaseGroups.find(
          (g) => g.name.toLowerCase() === name.toLowerCase()
        );

        if (!group) {
          console.warn(`⚠️ Loadcase "${name}" not found`);
          return;
        }

        set({ activeLoadcaseName: group.name });
      },

      getActiveLoadcase: () => {
        const state = get();
        if (!state.activeLoadcaseName) return null;
        
        return state.loadcaseGroups.find(
          (g) => g.name.toLowerCase() === state.activeLoadcaseName!.toLowerCase()
        ) || null;
      },

      addBeamLoadToGroup: (loadcaseName: string, beamLoadId: string) => {
        set((state) => ({
          loadcaseGroups: state.loadcaseGroups.map((g) =>
            g.name.toLowerCase() === loadcaseName.toLowerCase()
              ? {
                  ...g,
                  beamLoadIds: g.beamLoadIds.includes(beamLoadId)
                    ? g.beamLoadIds
                    : [...g.beamLoadIds, beamLoadId],
                }
              : g
          ),
        }));
      },

      removeBeamLoadFromGroup: (loadcaseName: string, beamLoadId: string) => {
        set((state) => ({
          loadcaseGroups: state.loadcaseGroups.map((g) =>
            g.name.toLowerCase() === loadcaseName.toLowerCase()
              ? {
                  ...g,
                  beamLoadIds: g.beamLoadIds.filter((id) => id !== beamLoadId),
                }
              : g
          ),
        }));
      },

      addNodeLoadToGroup: (loadcaseName: string, nodeLoadId: string) => {
        set((state) => ({
          loadcaseGroups: state.loadcaseGroups.map((g) =>
            g.name.toLowerCase() === loadcaseName.toLowerCase()
              ? {
                  ...g,
                  nodeLoadIds: g.nodeLoadIds.includes(nodeLoadId)
                    ? g.nodeLoadIds
                    : [...g.nodeLoadIds, nodeLoadId],
                }
              : g
          ),
        }));
      },

      removeNodeLoadFromGroup: (loadcaseName: string, nodeLoadId: string) => {
        set((state) => ({
          loadcaseGroups: state.loadcaseGroups.map((g) =>
            g.name.toLowerCase() === loadcaseName.toLowerCase()
              ? {
                  ...g,
                  nodeLoadIds: g.nodeLoadIds.filter((id) => id !== nodeLoadId),
                }
              : g
          ),
        }));
      },

      getLoadcaseByName: (name: string) => {
        return get().loadcaseGroups.find(
          (g) => g.name.toLowerCase() === name.toLowerCase()
        );
      },

      getAllLoadcaseNames: () => {
        return get().loadcaseGroups.map((g) => g.name);
      },

      getBeamLoadIdsForLoadcase: (name: string) => {
        const group = get().loadcaseGroups.find(
          (g) => g.name.toLowerCase() === name.toLowerCase()
        );
        return group?.beamLoadIds || [];
      },

      getNodeLoadIdsForLoadcase: (name: string) => {
        const group = get().loadcaseGroups.find(
          (g) => g.name.toLowerCase() === name.toLowerCase()
        );
        return group?.nodeLoadIds || [];
      },
    })
);
